/**
 * Utility functions for formatting token prices
 */

/**
 * Format a price with appropriate decimal places based on its value
 * @param price - The price to format
 * @param showDollarSign - Whether to include the dollar sign (default: true)
 * @returns Formatted price string
 */
export const formatPrice = (price: number, showDollarSign: boolean = true): string => {
  if (price === 0) return '0';
  
  const prefix = showDollarSign ? '$' : '';
  
  if (price >= 1) return `${prefix}${price.toFixed(2)}`;
  if (price >= 0.01) return `${prefix}${price.toFixed(4)}`;
  if (price >= 0.0001) return `${prefix}${price.toFixed(6)}`;
  if (price >= 0.000001) return `${prefix}${price.toFixed(8)}`;
  
  // For very small prices, use scientific notation
  const scientific = price.toExponential(2);
  return `${prefix}${scientific}`;
};

/**
 * Format a price for display in compact spaces (like trending bars)
 * @param price - The price to format
 * @returns Compact formatted price string
 */
export const formatPriceCompact = (price: number): string => {
  if (price === 0) return '0';
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.01) return `$${price.toFixed(3)}`;
  if (price >= 0.001) return `$${price.toFixed(4)}`;
  if (price >= 0.0001) return `$${price.toFixed(5)}`;
  if (price >= 0.00001) return `$${price.toFixed(6)}`;
  if (price >= 0.000001) return `$${price.toFixed(7)}`;
  if (price >= 0.0000001) return `$${price.toFixed(8)}`;
  
  // For extremely small prices, show up to 10 decimal places
  if (price >= 0.00000001) return `$${price.toFixed(10)}`;
  
  // For ultra-small prices, use scientific notation
  const scientific = price.toExponential(2);
  return `$${scientific}`;
};

/**
 * Format a price like the tokens page (no scientific notation).
 * @param price - The price to format
 * @returns Formatted price string or placeholder
 */
export const formatPriceDisplay = (price: number | null | undefined): string => {
  if (price === null || price === undefined || price === 0) return '--';
  if (price < 0.01) return `$${price.toFixed(7)}`;
  if (price < 1) return `$${price.toFixed(6)}`;
  return `$${price.toFixed(4)}`;
};

/**
 * Format a percentage change with appropriate sign and color indication
 * @param change - The percentage change (positive or negative)
 * @returns Formatted percentage string with sign
 */
export const formatPercentageChange = (change: number): string => {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
};
